import type {
  BuyerProductGetResponseType,
  UnifiedCheckoutBasketType,
} from "@sellar-io/api-v2/types";
import { useQueries } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../hooks/useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  apiV2Request,
  getDefaultQueryOptionsV2,
} from "../../../hooks/api/v2/apiV2Request";
import { ServerStateKeys } from "../../../hooks/api/v2/ServerStateKeys";
import type { ContextOrderBasketType } from "../../../hooks";
import { buildOrderItemFromMarketProduct } from "../../discovery/v2/distribution/buildOrderItemFromMarketProduct";
import { toISODateString } from "../../../types/isoDate";

export const useUnifiedCheckoutDraftOrders = (
  baskets: UnifiedCheckoutBasketType[] = [],
) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  const queries = useQueries({
    queries: baskets.map((basket) => ({
      queryKey: [
        user?.uid,
        business?.id,
        ServerStateKeys.UnifiedCheckoutBaskets,
        basket.uuid,
        "products",
      ],
      queryFn: () =>
        apiV2Request({
          user,
          business,
          method: "get" as const,
          path: `unified-checkout-baskets/${basket.uuid}/products`,
        }).then(
          (response) =>
            apiV2AssertResponse<BuyerProductGetResponseType[]>(response).data,
        ),
      ...getDefaultQueryOptionsV2<BuyerProductGetResponseType[]>({
        context: { scope: "buyer", user, business },
      }),
    })),
  });
  const productMap = new Map(
    queries.flatMap((query) =>
      (query.data ?? []).flatMap((parentProduct) =>
        parentProduct.Products.map(
          (product) => [product.id, parentProduct] as const,
        ),
      ),
    ),
  );
  const orders: ContextOrderBasketType[] = baskets.flatMap((entry) =>
    entry.DraftOrders.map((draft) => ({
      id: draft.id,
      SupplierId: draft.SupplierId,
      RetailerId: business?.Retailer?.id ?? null,
      fulfillmentType: "sellarDistribution",
      vatPercentage: draft.vatPercentage,
      currency: "gbp",
      initiator: "retailer",
      sdBasketUuid: entry.uuid,
      expectedDeliveryDate: draft.expectedDeliveryAt
        ? toISODateString(draft.expectedDeliveryAt.toString().slice(0, 10))
        : null,
      orderCutoffDate: draft.orderCutoffAt?.toString() ?? null,
      OrderItems: draft.Items.flatMap((item) => {
        const product = productMap.get(item.ProductId);
        const built = product
          ? buildOrderItemFromMarketProduct({
              productId: item.ProductId,
              fullProduct: product,
              quantity: item.quantity,
              itemId: item.id,
              promotion:
                item.PromotionRewardItemId != null &&
                item.promotionPrice != null
                  ? {
                      rewardItemId: item.PromotionRewardItemId,
                      price: item.promotionPrice,
                    }
                  : null,
            })
          : null;
        return built ? [built] : [];
      }),
    })),
  );
  return {
    orders,
    isLoading: queries.some((query) => query.isLoading),
  };
};
